import { useEffect, useState } from "react";

import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";
import { useTrialEligibility } from "@ui/providers/TrialEligibilityContext";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import type { ButtonProps } from "@jti/core-components/components/Button";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { useMessageBus } from "@jti/fe-message-bus";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useAddProduct } from "../../hooks/useAddProduct";
import { useUser } from "../../hooks/useUser";

import "./style.scss";

type ProductTryNowButtonProps = {
  sku: string;
  label: string;
  isBundle: boolean;
  productPageUrl?: string;
  size?: ButtonProps["size"];
  stretch?: boolean;
  categoryId?: string;
  hasLoadingOverlay?: boolean;
};

export const ProductTryNowButton = ({
  sku,
  label,
  isBundle,
  productPageUrl,
  size,
  stretch = false,
  categoryId = "",
  hasLoadingOverlay = false,
}: ProductTryNowButtonProps) => {
  const trialEligibility = useTrialEligibility();
  const messageBus = useMessageBus();
  const { FREE_TRIAL_PAGE_URL } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();

  const { isRunning, addProductToCart } = useAddProduct(sku, [], true);
  const { isUserLoggedIn } = useUser();
  const [isCartLoaded, setIsCartLoaded] = useState(false);

  useEffect(() => {
    messageBus.subscribe({
      channel: "cart.loaded",
      callback: () => {
        setIsCartLoaded(true);
      },
    });
  }, [messageBus]);

  const handleTryButtonClick = () => {
    if (isUserLoggedIn || isBundle) {
      dispatchTrackingEvent(TrackingEvents.TrackingFreeTrialStart);
      addProductToCart();
    } else {
      window.location.href = resolveAuthHref({
        route: AUTH_ROUTE_PATHS.login,
        search: { returnUrl: FREE_TRIAL_PAGE_URL },
      });
    }
  };

  const isLoadingEligibility = trialEligibility ? trialEligibility.isLoading : false;
  const isEligible = trialEligibility
    ? !trialEligibility.isLoading && trialEligibility.eligibleCategoryIds.includes(categoryId)
    : true;

  const renderButton = () => {
    if (!isEligible) {
      return null;
    }

    if (isBundle) {
      return (
        <Button
          pending={(!isCartLoaded && !!isUserLoggedIn) || isLoadingEligibility}
          variant="primary"
          data-testid="teaserTryNowButton"
          className={"product-teaser-try-now__button"}
          href={productPageUrl}
          size={size}
        >
          {label}
        </Button>
      );
    }
    return (
      <Button
        pending={(!isCartLoaded && !!isUserLoggedIn) || isLoadingEligibility}
        variant="primary"
        onClick={handleTryButtonClick}
        data-testid="teaserTryNowButton"
        className={"product-teaser-try-now__button"}
        size={size}
      >
        {label}
      </Button>
    );
  };

  return (
    <div className={classNames("product-teaser-try-now", { ["product-teaser-try-now--stretch"]: stretch })}>
      {hasLoadingOverlay && isRunning ? (
        <div className="product-teaser-try-now__loader-overlay">
          <Loader />
        </div>
      ) : (
        renderButton()
      )}
    </div>
  );
};
